import { cn } from "@/utils/cn";
import type { Position, Selection } from "./model";

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

export function CompetencyStep({
  position,
  selection,
  onToggle,
  onToggleRequired,
}: {
  position: Position;
  selection: Selection;
  onToggle: (id: string) => void;
  onToggleRequired: (id: string) => void;
}) {
  const selectedSet = new Set(selection.competencyIds);
  const requiredSet = new Set(selection.requiredIds);

  return (
    <div className="trial-fade-in">
      <h3 className="mb-1 text-[17px] font-bold tracking-tight text-[var(--trial-foreground)]">핵심 역량을 골라주세요</h3>
      <p className="mb-4 text-[13px] text-[var(--trial-muted)]">
        필요한 역량을 여러 개 고르고, 그중 <strong className="text-[var(--trial-foreground)]">꼭 필요한 건 ⭐로 필수 지정</strong>
        하세요. 나머지는 우대로 평가합니다.
      </p>

      <div className="flex flex-wrap gap-2">
        {position.competencies.map((competency) => {
          const selected = selectedSet.has(competency.id);
          const required = requiredSet.has(competency.id);
          return (
            <div
              key={competency.id}
              className={cn(
                "inline-flex items-center rounded-full transition-all hover:-translate-y-0.5",
                required
                  ? "trial-brand-gradient text-white shadow-[0_4px_14px_-3px_rgba(79,109,245,0.5)]"
                  : selected
                    ? "bg-[var(--trial-brand-bg)] text-[var(--trial-brand)] ring-1 ring-[var(--trial-brand-soft)]"
                    : "trial-glass-subtle text-[var(--trial-muted-strong)] hover:text-[var(--trial-foreground)]",
              )}
            >
              <button
                type="button"
                onClick={() => onToggle(competency.id)}
                className="py-2 pl-3.5 pr-2 text-[13px] font-medium"
                title={competency.desc}
              >
                {!selected && <span className="mr-0.5 opacity-60">+</span>}
                {competency.name}
              </button>
              {selected && (
                <button
                  type="button"
                  onClick={() => onToggleRequired(competency.id)}
                  aria-label={required ? "필수 해제" : "필수로 지정"}
                  title={required ? "필수 → 우대" : "우대 → 필수"}
                  className={cn(
                    "py-2 pl-1 pr-3",
                    required ? "text-white" : "text-[var(--trial-brand)]/55 hover:text-[var(--trial-brand)]",
                  )}
                >
                  <StarIcon filled={required} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 text-[12px] text-[var(--trial-muted)]">
        선택 <strong className="font-mono font-semibold text-[var(--trial-foreground)]">{selection.competencyIds.length}</strong>
        개<span className="mx-1 opacity-40">·</span>
        필수 <strong className="font-mono font-semibold text-[var(--trial-foreground)]">{selection.requiredIds.length}</strong>개
        {selection.competencyIds.length === 0 && <span className="ml-2 text-[var(--trial-brand)]">최소 1개 이상 골라주세요</span>}
      </div>
    </div>
  );
}
